/**
 * MediaCache
 * ----------
 * A session-scoped, in-memory cache for uploaded media (images and direct
 * video files) shown on a TV display.
 *
 * Why this exists:
 *   The playlist rotates through content blocks. Previously, when a block
 *   expired its media element was unmounted from the DOM, and when the block
 *   cycled back the browser re-requested the file from the network — showing a
 *   loading spinner and making the screen dependent on connectivity.
 *
 *   This cache fetches each media URL exactly once into a Blob, hands out an
 *   object URL (`blob:` URL) that is served from memory, and reuses it for the
 *   entire session. Media therefore stays in the browser and is never reloaded
 *   from the network, even on slow or intermittent connections.
 *
 * Memory management ("keep the largest pieces"):
 *   Older TVs (Samsung Tizen, LG webOS, etc.) have limited memory, so the cache
 *   enforces a soft byte budget. When the budget is exceeded, the SMALLEST
 *   cached items are evicted first, which keeps the largest files resident —
 *   those are the most expensive to re-download and cause the longest loading
 *   delays. Small files evict cheaply and re-fetch quickly when needed. Items
 *   currently on screen are "pinned" and never evicted.
 *
 * TV-browser safety:
 *   - Everything degrades gracefully. If `fetch` / `Blob` / `createObjectURL`
 *     are unavailable, or a fetch fails (CORS, offline, 404), the cache reports
 *     a miss and the caller renders the plain network URL — i.e. exactly the
 *     old behavior, never a crash.
 *   - A per-item size cap (checked against `Content-Length` BEFORE downloading)
 *     stops a single huge video from being pulled into memory and OOM-ing a
 *     low-RAM TV. Oversized files simply stream from the network as usual.
 *   - The APIs used here (fetch, Blob, object URLs) are already used elsewhere
 *     in this app, so they are within the browser baseline the displays run.
 *
 * The cache is intentionally framework-agnostic (no React) so it can be driven
 * from hooks, preloaders, or plain code. It is a session singleton — nothing is
 * persisted to disk, so a full page reload starts fresh.
 */

interface MediaCacheEntry {
  url: string;
  objectUrl: string;
  size: number;
  lastUsedAt: number;
  /** Number of live consumers (e.g. an on-screen media element). Pinned while > 0. */
  refCount: number;
}

const MB = 1024 * 1024;

/**
 * Whether this browser supports the APIs the cache needs. On anything that
 * doesn't (very old/limited engines), the cache stays inert and callers fall
 * back to plain network URLs.
 */
const isSupported = (): boolean => {
  try {
    return (
      typeof fetch === "function" &&
      typeof Blob !== "undefined" &&
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL === "function" &&
      typeof URL.revokeObjectURL === "function"
    );
  } catch {
    return false;
  }
};

/**
 * Determine a sensible byte budget for this device.
 *
 * `navigator.deviceMemory` reports approximate RAM in GB (0.25 – 8, capped).
 * It is undefined on many TV browsers, in which case we fall back to a
 * conservative default. We only ever allocate a fraction of RAM to blob media.
 */
const detectDefaultBudget = (): number => {
  const deviceMemoryGb = (navigator as unknown as { deviceMemory?: number })
    .deviceMemory;

  if (typeof deviceMemoryGb === "number" && deviceMemoryGb > 0) {
    // ~150 MB per GB of RAM, clamped to a safe range for low-end hardware.
    const budgetMb = Math.min(Math.max(deviceMemoryGb * 150, 96), 768);
    return budgetMb * MB;
  }

  // Unknown device (common on older TVs) — stay conservative.
  return 200 * MB;
};

/**
 * Largest single file we will hold in memory. A blob-backed `<video>` has to be
 * fully downloaded before it can play, so an oversized video is both a memory
 * risk and slow to start — better to let those stream from the network. Capped
 * to half the budget (so one item can't dominate) and never more than 128 MB.
 */
const computeMaxItemBytes = (budgetBytes: number): number =>
  Math.min(Math.floor(budgetBytes * 0.5), 128 * MB);

const isCacheableUrl = (url: string | null | undefined): url is string => {
  if (!url) return false;
  // Only network-fetchable http(s) URLs benefit from caching. Inline data URIs
  // and existing blob URLs are already local; anything else we leave untouched.
  return /^https?:\/\//i.test(url);
};

export class MediaCache {
  private entries = new Map<string, MediaCacheEntry>();
  /** In-flight fetches, so concurrent requests for the same URL share one download. */
  private pending = new Map<string, Promise<string | null>>();
  /** URLs we won't cache (failed fetch, too large, or a bad blob). Passed through. */
  private uncacheable = new Set<string>();
  private currentBytes = 0;
  private budgetBytes: number;
  private maxItemBytes: number;
  private readonly supported = isSupported();

  constructor(budgetBytes?: number) {
    this.budgetBytes = budgetBytes ?? detectDefaultBudget();
    this.maxItemBytes = computeMaxItemBytes(this.budgetBytes);
  }

  /** Whether caching is active in this browser. */
  isEnabled(): boolean {
    return this.supported;
  }

  /** Override the memory budget (bytes). Triggers eviction if now over budget. */
  setBudget(budgetBytes: number): void {
    this.budgetBytes = budgetBytes;
    this.maxItemBytes = computeMaxItemBytes(budgetBytes);
    this.evictToBudget();
  }

  getBudget(): number {
    return this.budgetBytes;
  }

  /** True if the URL is already resident in memory. */
  has(url: string): boolean {
    return this.entries.has(url);
  }

  /**
   * Return the cached object URL for an already-resident file, or undefined.
   * Touches the entry's recency. Never triggers a fetch.
   */
  getObjectUrl(url: string): string | undefined {
    const entry = this.entries.get(url);
    if (!entry) return undefined;
    entry.lastUsedAt = Date.now();
    return entry.objectUrl;
  }

  /**
   * Ensure the URL is resident and return its object URL.
   *
   * Resolves to:
   *   - a `blob:` object URL when the file was (or already is) cached, or
   *   - `null` when the URL is not cacheable, too large, or the fetch failed.
   *     Callers should fall back to the original URL (normal network loading).
   */
  async load(url: string): Promise<string | null> {
    if (!this.supported) return null;
    if (!isCacheableUrl(url) || this.uncacheable.has(url)) {
      return null;
    }

    const existing = this.entries.get(url);
    if (existing) {
      existing.lastUsedAt = Date.now();
      return existing.objectUrl;
    }

    const inFlight = this.pending.get(url);
    if (inFlight) return inFlight;

    const promise = this.fetchAndStore(url);
    this.pending.set(url, promise);

    try {
      return await promise;
    } finally {
      this.pending.delete(url);
    }
  }

  private async fetchAndStore(url: string): Promise<string | null> {
    try {
      const response = await fetch(url, {
        // `credentials: omit` maximizes the chance the CDN's CORS policy allows
        // the read. Uploaded media is served from public URLs.
        mode: "cors",
        credentials: "omit",
        cache: "force-cache",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Reject oversized files up front, before downloading them into memory.
      // `Content-Length` is a CORS-safelisted response header, so it is readable
      // cross-origin without extra server config.
      const declaredSize = Number(response.headers.get("content-length"));
      if (Number.isFinite(declaredSize) && declaredSize > this.maxItemBytes) {
        this.uncacheable.add(url);
        return null;
      }

      const blob = await response.blob();

      // Guard again in case Content-Length was missing or wrong.
      if (blob.size > this.maxItemBytes) {
        this.uncacheable.add(url);
        return null;
      }

      // A concurrent call may have stored it while we were downloading.
      const existing = this.entries.get(url);
      if (existing) {
        existing.lastUsedAt = Date.now();
        return existing.objectUrl;
      }

      const objectUrl = URL.createObjectURL(blob);
      const entry: MediaCacheEntry = {
        url,
        objectUrl,
        size: blob.size,
        lastUsedAt: Date.now(),
        refCount: 0,
      };

      this.entries.set(url, entry);
      this.currentBytes += entry.size;
      this.evictToBudget();

      // If eviction removed the entry we just added (it was the smallest and
      // over budget), report a cache miss so the caller uses the network URL.
      if (!this.entries.has(url)) {
        return null;
      }

      return objectUrl;
    } catch {
      // CORS failure, offline, 404, etc. Remember so we don't hammer the network
      // on every re-appearance; the caller falls back to the raw URL.
      this.uncacheable.add(url);
      return null;
    }
  }

  /**
   * Mark a URL as unusable — e.g. a media element failed to render its cached
   * blob (a picky TV decoder). Evicts the blob and stops caching this URL so the
   * caller streams it from the network instead.
   */
  markUnusable(url: string): void {
    const entry = this.entries.get(url);
    if (entry) {
      this.evictEntry(entry);
    }
    this.uncacheable.add(url);
  }

  /**
   * Mark a URL as in active use (e.g. rendered on screen) so it is never evicted.
   * Balance every pin() with an unpin().
   */
  pin(url: string): void {
    const entry = this.entries.get(url);
    if (entry) {
      entry.refCount += 1;
      entry.lastUsedAt = Date.now();
    }
  }

  unpin(url: string): void {
    const entry = this.entries.get(url);
    if (entry && entry.refCount > 0) {
      entry.refCount -= 1;
    }
  }

  /**
   * Eagerly fetch a set of media URLs into the cache with limited concurrency.
   * Already-cached, pending, or uncacheable URLs are skipped. Runs in the
   * background; failures are swallowed (they simply won't be cached).
   */
  async preload(
    urls: Array<string | null | undefined>,
    concurrency = 2,
  ): Promise<void> {
    if (!this.supported) return;

    const queue = urls.filter(
      (url): url is string =>
        isCacheableUrl(url) &&
        !this.entries.has(url) &&
        !this.pending.has(url) &&
        !this.uncacheable.has(url),
    );

    // De-duplicate while preserving order.
    const unique = Array.from(new Set(queue));

    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < unique.length) {
        const url = unique[cursor++];
        await this.load(url);
      }
    };

    const workers = Array.from(
      { length: Math.min(concurrency, unique.length) },
      () => worker(),
    );
    await Promise.all(workers);
  }

  /**
   * Evict the smallest, non-pinned entries until we are within budget.
   * Keeping the largest files resident is intentional: they are the costliest
   * to re-download and cause the worst loading delays on slow links.
   */
  private evictToBudget(): void {
    if (this.currentBytes <= this.budgetBytes) return;

    // Evictable = not currently pinned (on screen).
    const evictable = Array.from(this.entries.values()).filter(
      (entry) => entry.refCount === 0,
    );

    // Smallest first; break ties by least-recently-used.
    evictable.sort((a, b) =>
      a.size !== b.size ? a.size - b.size : a.lastUsedAt - b.lastUsedAt,
    );

    for (const entry of evictable) {
      if (this.currentBytes <= this.budgetBytes) break;
      this.evictEntry(entry);
    }
    // If still over budget, everything left is pinned — accept temporary
    // overflow rather than dropping media that is currently on screen.
  }

  private evictEntry(entry: MediaCacheEntry): void {
    URL.revokeObjectURL(entry.objectUrl);
    this.entries.delete(entry.url);
    this.currentBytes -= entry.size;
  }

  /** Diagnostics for debugging / dev overlays. */
  stats(): {
    count: number;
    bytes: number;
    budget: number;
    maxItemBytes: number;
    pinned: number;
    enabled: boolean;
  } {
    let pinned = 0;
    for (const entry of this.entries.values()) {
      if (entry.refCount > 0) pinned += 1;
    }
    return {
      count: this.entries.size,
      bytes: this.currentBytes,
      budget: this.budgetBytes,
      maxItemBytes: this.maxItemBytes,
      pinned,
      enabled: this.supported,
    };
  }

  /** Revoke every object URL and reset. */
  clear(): void {
    for (const entry of this.entries.values()) {
      URL.revokeObjectURL(entry.objectUrl);
    }
    this.entries.clear();
    this.pending.clear();
    this.uncacheable.clear();
    this.currentBytes = 0;
  }
}

/** Session singleton used across the display. */
export const mediaCache = new MediaCache();
